import React, { createContext, useContext, useReducer } from "react";
import { v4 as uuidv4 } from "uuid";

interface FormatContextState {
  currentFormat: any | null;
  recentFormats: any[];
  selectedFields: any[]; // multiple selected fields
  selectedField: any | null; // field being configured
  activeStep: number;
  confidenceScore: number;
  testingResults: {
    allFieldsWorking: boolean;
    validationRulesOk: boolean;
    mobileLayoutGood: boolean;
    customerDbSlow: boolean;
  };
}

const createInitialState = (): FormatContextState => ({
  currentFormat: null,
  recentFormats: [],
  selectedFields: [],
  selectedField: null,
  activeStep: 0,
  confidenceScore: 87,
  testingResults: {
    allFieldsWorking: true,
    validationRulesOk: true,
    mobileLayoutGood: true,
    customerDbSlow: true,
  },
});

type FormatAction =
  | {
      type: "CREATE_FORMAT";
      payload: { name: string; utilityService: any; readType: any };
    }
  | { type: "TOGGLE_FIELD"; payload: { field: any } }
  | { type: "UPDATE_FIELD"; payload: { field: any } }
  | { type: "SELECT_FIELD"; payload: { field: any | null } }
  | { type: "SET_STEP"; payload: { step: number } }
  | { type: "SET_ALL_FIELDS"; payload: { fields: any[] } }
  | { type: "SAVE_FORMAT" };

const formatReducer = (
  state: FormatContextState,
  action: FormatAction
): FormatContextState => {
  switch (action.type) {
    case "CREATE_FORMAT": {
      return {
        ...state,
        currentFormat: {
          id: uuidv4(),
          name: action.payload.name,
          utilityService: action.payload.utilityService,
          readType: action.payload.readType,
          fields: [...state.selectedFields],
        },
      };
    }

    case "TOGGLE_FIELD": {
      const field = action.payload.field;
      const exists = state.selectedFields.some((f) => f.id === field.id);

      return {
        ...state,
        selectedFields: exists
          ? state.selectedFields.filter((f) => f.id !== field.id)
          : [...state.selectedFields, { ...field, isSelected: true }],
      };
    }

    case "UPDATE_FIELD": {
      const updatedFields = state.selectedFields.map((f) =>
        f.id === action.payload.field.id ? action.payload.field : f
      );

      // Save to localStorage
      localStorage.setItem(
        "format_selected_fields",
        JSON.stringify(updatedFields)
      );

      return {
        ...state,
        selectedFields: updatedFields,
        selectedField: action.payload.field, // keep selectedField in sync too
      };
    }

    case "SELECT_FIELD": {
      const field = action.payload.field;
      const exists = field
        ? state.selectedFields.some((f) => f.id === field.id)
        : false;

      return {
        ...state,
        selectedFields:
          field && !exists
            ? [...state.selectedFields, { ...field, isSelected: true }]
            : [...state.selectedFields],
        selectedField: field,
      };
    }

    case "SET_STEP":
      return { ...state, activeStep: action.payload.step };

    case "SAVE_FORMAT": {
      if (!state.currentFormat) return state;

      const updatedFormat = {
        ...state.currentFormat,
        fields: [...state.selectedFields],
        lastModified: new Date(),
        isActive: true,
      };

      return {
        ...state,
        currentFormat: updatedFormat,
        recentFormats: [updatedFormat, ...state.recentFormats],
        activeStep: 0,
      };
    }
    case "SET_ALL_FIELDS": {
      return {
        ...state,
        selectedFields: action.payload.fields,
      };
    }

    default:
      return state;
  }
};

// Context
const FormatContext = createContext<
  | {
      state: FormatContextState;
      dispatch: React.Dispatch<FormatAction>;
    }
  | undefined
>(undefined);

// Provider
export const FormatProvider = ({ children }: { children: React.ReactNode }) => {
  const [state, dispatch] = useReducer(formatReducer, createInitialState());

  return (
    <FormatContext.Provider value={{ state, dispatch }}>
      {children}
    </FormatContext.Provider>
  );
};

// Hook
export const useFormat = () => {
  const context = useContext(FormatContext);
  if (!context) {
    throw new Error("useFormat must be used within a FormatProvider");
  }
  return context;
};
